import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux'

import * as StepLabels from '../constants/StepLabels'
import StepSelectionButton from '../components/navigation/StepSelectionButton'
import {isClientDataStepValid, isConfigurationStepValid, isOutsourcingTargetStepValid, isPaymentSettingsValid, isProductChangeStepValid, isProductSelectionStepValid} from '../selectors/StepValidation'
import {forceValidationOnSteps, gotoNextStep, selectStep} from "../actions/StepControl";
import {isExistingUserAuthenticatedAndSelected} from "../selectors/UserInterface";
import {isMode} from "../selectors/Mode";
import {APP_MODE_AUTHENTICATED_ORDER, APP_MODE_OUTSOURCING, APP_MODE_PRODUCT_CHANGE} from "../constants/General";


class StepSelection extends Component {

    static propTypes = {
        stepConfig: PropTypes.object.isRequired,
        stepsCompleted: PropTypes.array,
        currentStep: PropTypes.string.isRequired,
        onSelectStep: PropTypes.func
    }

    constructor(props) {
        super(props);
        this.state = {markerWidth: 0};

        this.selectionRef = React.createRef();
        this.activeRef = React.createRef();
    }

    getCompletedStepIndex = (key) => {

        return this.props.stepsCompleted.findIndex((element) => {
            return element === key
        })

    }

    stepAvailable = (stepToCheck) => {

        const stepsCompletedFiltered = this.props.stepsCompleted.filter(elem => {
            return !!this.props.stepConfig[elem];
        })

        let stepIndex = Object.keys(this.props.stepConfig).indexOf(stepToCheck);
        let previousStep = false;

        if (stepIndex > 0) {

            previousStep = Object.keys(this.props.stepConfig)[stepIndex - 1];
            return stepsCompletedFiltered.indexOf(previousStep) > 0 || (previousStep === this.props.currentStep) || stepsCompletedFiltered.indexOf(stepToCheck) > 0

        } else {
            return true;
        }

    }

    /**
     * Receive the first step that needs to be validated before target step can be chosen
     * @param key
     */
    getFirstInvalidStepKey = (key) => {

        if (isMode(APP_MODE_PRODUCT_CHANGE) && this.props.productChangeStepValid !== true) {
            return StepLabels.PRODUCT_CHANGE
        }

        if (isMode(APP_MODE_AUTHENTICATED_ORDER) && this.props.productSelectionStepValid !== true) {
            return StepLabels.PRODUCT_SELECTION
        }

        if (isMode(APP_MODE_OUTSOURCING) && this.props.outsourcingTargetStepValid !== true) {
            return StepLabels.OUTSOURCING_TARGET
        }

        if (!this.props.configurationStepValid && this.props.stepConfig[StepLabels.CONFIG]) {
            return StepLabels.CONFIG
        }

        if (!this.props.clientDataStepValid) {
            if ([StepLabels.SUMMARY, StepLabels.PAYMENT].includes(key)) {
                return StepLabels.CLIENTDATA
            }
        }

        if (!this.props.isExistingUserAuthenticatedAndSelected) {

            // when trying to reach summary step, payment step is not available

            if (!this.props.isPaymentSettingsValid) {
                if ([StepLabels.SUMMARY].includes(key)) {
                    return StepLabels.PAYMENT
                }
            }

        }

        return false;

    }


    handleStepSelectionClick = (key) => {
        if (this.stepAvailable(key)) {
            this.props.onSelectStep(key)
        }
    }

    setMarkerWidth() {

        const selection = this.selectionRef.current;
        const activeButton = this.activeRef.current;

        const targetWidth = activeButton.getBoundingClientRect().left - selection.getBoundingClientRect().left + activeButton.getBoundingClientRect().width;
        const selectionWidth = selection.getBoundingClientRect().width;

        const markerWidth = ((targetWidth / selectionWidth) * 100);

        this.setState({markerWidth: markerWidth})

    }

    componentDidMount() {
        this.setMarkerWidth()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevProps.currentStep !== this.props.currentStep
            ||
            prevProps.stepConfig !== this.props.stepConfig
            ||
            prevProps.stepsCompleted !== this.props.stepsCompleted
        ) {
            this.setMarkerWidth()
        }
    }

    render() {

        let buttons = []
        let buttonIndex = 0

        for (let [key, value] of Object.entries(this.props.stepConfig)) {

            buttons.push(
                <StepSelectionButton
                    key={key}
                    index={buttonIndex++}
                    stepLabel={key}
                    buttonLabel={value.title}
                    buttonIcon={value.icon}
                    invalidStepJump={
                        this.getFirstInvalidStepKey(key) &&
                        this.props.stepOrder.indexOf(key) > this.props.stepOrder.indexOf(this.getFirstInvalidStepKey(key))
                            ? this.props.stepConfig[this.getFirstInvalidStepKey(key)].title
                            : ""
                    }
                    invalidStepJumpFirstTime={!this.props.stepsCompleted.includes(this.getFirstInvalidStepKey(key))}
                    isAvailable={this.stepAvailable(key)}
                    isCompleted={this.props.stepsCompleted.includes(key)}
                    isActive={this.props.currentStep === key}
                    forwardedRef={this.props.currentStep === key ? this.activeRef : null}
                    isDisabled={this.props.isUserAuthenticated && key === StepLabels.PAYMENT}
                    onStepSelectionClick={() => {
                        this.handleStepSelectionClick(key)
                    }}
                    onInvalidStepClick={
                        () => {
                            let forcedValidationSteps = []

                            if (this.props.outsourcingTargetStepValid !== true) {
                                forcedValidationSteps.push(StepLabels.OUTSOURCING_TARGET)
                            }

                            if (this.props.productChangeStepValid !== true) {
                                forcedValidationSteps.push(StepLabels.PRODUCT_CHANGE)
                            }

                            if (this.props.productSelectionStepValid !== true) {
                                forcedValidationSteps.push(StepLabels.PRODUCT_SELECTION)
                            }

                            if (!this.props.configurationStepValid) {
                                forcedValidationSteps.push(StepLabels.CONFIG)
                            }

                            if (!this.props.clientDataStepValid) {
                                forcedValidationSteps.push(StepLabels.CLIENTDATA)
                            }

                            if (!this.props.isPaymentSettingsValid) {
                                forcedValidationSteps.push(StepLabels.PAYMENT)
                            }

                            this.props.forceValidationOnSteps(forcedValidationSteps)
                        }
                    }
                    onInvalidStepHover={null}
                />
            )

        }

        return (
            <nav className={"step-selection step-selection--" + buttons.length} ref={this.selectionRef}>
                <div className={"step-selection__buttons"}>
                    {buttons}
                </div>
                <div className={"step-selection__marker"} style={{width: this.state.markerWidth + "%"}}/>
            </nav>
        )

    }

}

const mapStateToProps = (state, ownProps) => {

    return {
        configurationStepValid: isConfigurationStepValid(state),
        stepsCompleted: state.ui.stepsCompleted,
        currentStep: state.ui.currentStep,
        isPaymentSettingsValid: isPaymentSettingsValid(state),
        clientDataStepValid: isClientDataStepValid(state),
        isUserAuthenticated: state.clientDataStep.clientData !== false,
        isExistingUserAuthenticatedAndSelected: isExistingUserAuthenticatedAndSelected(state),
        outsourcingTargetStepValid: isOutsourcingTargetStepValid(state),
        productSelectionStepValid: isProductSelectionStepValid(state),
        productChangeStepValid: isProductChangeStepValid(state),
    }

}

const mapDispatchToProps = (dispatch, ownProps) => ({
    onSelectStep: (key) => {
        dispatch(selectStep(key))
    },
    onSelectNext: () => {
        dispatch(gotoNextStep())
    },
    forceValidationOnSteps: (steps) => {
        dispatch(forceValidationOnSteps(steps))
    }
})


export default connect(mapStateToProps, mapDispatchToProps)(StepSelection)



/** WEBPACK FOOTER **
 ** ./src/containers/StepSelection.js
 **/